/**
 * ShareStore Download Service
 *
 * Handles document download operations including:
 * - Single file downloads
 * - Multi-file zip downloads
 * - Folder structure preservation in zips
 */

import JSZip from "jszip";
import type { ShareDocument, ShareFolderNode, ShareDetails } from "./models";
import { ensureFileExtension, makeUniqueFilename, sanitizeFilename } from "./utils";
import { track } from "@/lib/posthog";
import { fileExtProp } from "@/lib/analytics/eventProps";

// ============================================================================
// Types
// ============================================================================

export type ToastFn = (opts: {
  title: string;
  description?: string;
  variant?: "default" | "destructive";
}) => void;

interface FileToDownload {
  doc: ShareDocument;
  path: string;
}

/**
 * Files to bundle plus every folder path in the selection — including folders
 * with no documents, which must become explicit directory entries in the zip
 * (zips only represent directories that contain an entry).
 */
export interface CollectedDownload {
  files: FileToDownload[];
  folderPaths: string[];
}

interface DownloadResult {
  success: boolean;
  successCount: number;
  failCount: number;
}

// ============================================================================
// Single File Download
// ============================================================================

/**
 * Download a single document directly (no zip)
 */
export async function downloadSingleDocument(doc: ShareDocument, toast: ToastFn): Promise<boolean> {
  if (!doc.signedUrl) {
    toast({
      title: "Download failed",
      description: "No download URL available",
      variant: "destructive",
    });
    return false;
  }

  try {
    const response = await fetch(doc.signedUrl);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], {
      type: doc.content_type || "application/octet-stream",
    });
    const blobUrl = URL.createObjectURL(blob);

    const filename = ensureFileExtension(doc.filename, doc.content_type);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    track("document_downloaded", { file_ext: fileExtProp(filename), surface: "share" });
    return true;
  } catch (err) {
    console.error("Download failed:", err);
    toast({
      title: "Download failed",
      description: err instanceof Error ? err.message : "Please try again",
      variant: "destructive",
    });
    return false;
  }
}

// ============================================================================
// File Collection Helpers
// ============================================================================

/**
 * Recursively gather a folder subtree's files (tagged with their folder path)
 * and every folder path itself, so empty folders survive the zip round-trip.
 */
function collectFolderSubtree(
  folder: ShareFolderNode,
  parentPath: string,
  files: FileToDownload[],
  folderPaths: string[]
): void {
  const folderPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
  folderPaths.push(folderPath);
  for (const doc of folder.documents) {
    files.push({ doc, path: folderPath });
  }
  for (const child of folder.children) {
    collectFolderSubtree(child, folderPath, files, folderPaths);
  }
}

/**
 * Collect all files from share with their folder paths
 */
export function collectAllFiles(shareDetails: ShareDetails): CollectedDownload {
  const files: FileToDownload[] = [];
  const folderPaths: string[] = [];

  // If there are folders, preserve structure
  if (shareDetails.folderTree.length > 0) {
    // Root documents
    for (const doc of shareDetails.rootDocuments || []) {
      files.push({ doc, path: "" });
    }
    // Folder contents
    for (const folder of shareDetails.folderTree) {
      collectFolderSubtree(folder, "", files, folderPaths);
    }
  } else {
    // Flat list
    for (const doc of shareDetails.documents) {
      files.push({ doc, path: "" });
    }
  }

  return { files, folderPaths };
}

/**
 * Collect files from specific folders (for folder selection)
 */
export function collectFilesFromFolders(
  folderTree: ShareFolderNode[],
  selectedFolderIds: string[]
): CollectedDownload {
  const files: FileToDownload[] = [];
  const folderPaths: string[] = [];

  const findSelectedFolders = (folders: ShareFolderNode[]) => {
    for (const folder of folders) {
      if (selectedFolderIds.includes(folder.id)) {
        collectFolderSubtree(folder, "", files, folderPaths);
      } else {
        findSelectedFolders(folder.children);
      }
    }
  };

  findSelectedFolders(folderTree);
  return { files, folderPaths };
}

/**
 * Collect files from document IDs (flat, no folder structure)
 */
export function collectFilesFromIds(
  documentMap: Map<string, ShareDocument>,
  docIds: string[]
): FileToDownload[] {
  const files: FileToDownload[] = [];
  for (const docId of docIds) {
    const doc = documentMap.get(docId);
    if (doc) {
      files.push({ doc, path: "" });
    }
  }
  return files;
}

// ============================================================================
// Zip Download
// ============================================================================

/**
 * Download multiple files as a zip, materializing every collected folder path
 * as an explicit directory entry so empty folders are preserved.
 */
export async function downloadFilesAsZip(
  download: CollectedDownload,
  zipFilename: string,
  toast: ToastFn,
  onProgress?: (message: string) => void
): Promise<DownloadResult> {
  const { files, folderPaths } = download;
  const zip = new JSZip();
  let successCount = 0;
  let failCount = 0;

  for (const folderPath of folderPaths) {
    zip.folder(folderPath);
  }

  // Track used names per folder path to avoid duplicates
  const usedNamesPerPath = new Map<string, Set<string>>();

  onProgress?.(`Processing ${files.length} files...`);

  // Download and add each file to zip
  for (const { doc, path } of files) {
    if (!doc.signedUrl) {
      failCount++;
      continue;
    }

    try {
      const response = await fetch(doc.signedUrl);
      if (!response.ok) {
        failCount++;
        continue;
      }

      const arrayBuffer = await response.arrayBuffer();
      const filename = ensureFileExtension(doc.filename, doc.content_type);

      // Get or create used names set for this path
      if (!usedNamesPerPath.has(path)) {
        usedNamesPerPath.set(path, new Set<string>());
      }
      const usedNames = usedNamesPerPath.get(path)!;
      const uniqueName = makeUniqueFilename(filename, usedNames);

      const fullPath = path ? `${path}/${uniqueName}` : uniqueName;
      zip.file(fullPath, arrayBuffer);
      successCount++;
    } catch {
      failCount++;
    }
  }

  // Generate and download zip. A selection of only empty folders (no files,
  // nothing failed) still delivers a zip carrying the directory structure.
  if (successCount > 0 || (failCount === 0 && folderPaths.length > 0)) {
    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipUrl = URL.createObjectURL(zipBlob);

      const link = document.createElement("a");
      link.href = zipUrl;
      link.download = `${sanitizeFilename(zipFilename)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(zipUrl);

      toast({
        title: "Download complete",
        description:
          failCount > 0
            ? `${successCount} files downloaded, ${failCount} failed`
            : successCount > 0
              ? `${successCount} files downloaded`
              : "Folder structure downloaded",
      });

      return { success: true, successCount, failCount };
    } catch (err) {
      console.error("Zip generation failed:", err);
      toast({
        title: "Download failed",
        description: "Failed to create zip file",
        variant: "destructive",
      });
      return { success: false, successCount: 0, failCount: files.length };
    }
  } else {
    toast({
      title: "Download failed",
      description: "No files could be downloaded",
      variant: "destructive",
    });
    return { success: false, successCount, failCount };
  }
}
